<script setup lang="ts">
import { computed, ref } from 'vue';
import BadgePinned from '@/assets/images/badge_pinned.svg';
import UiMicroAppItem from '@/components/LayoutV2/UiMicroAppItem.vue';
import useMatestoreStore from '@/store/matestoreStore';
import { useMicroAppItemActions } from '@/components/LayoutV2/useMicroAppItemAction';
import { debounce } from 'lodash-es';
import { type SortingItem } from '@/api/model/globalModel';
import { NIcon } from '@nio-fe/icon';
import PerfectScrollbar from '@/components/perfect-scrollbar/index.vue';

const microStore = useMatestoreStore();
const { handlePin, handleUnpin, handleClickMicroApp } =
  useMicroAppItemActions();
const keywordRef = ref('');
const inputRef = ref();
const drawerVisible = ref(false);

const debSearching = debounce((e: any) => {
  console.log('searching', e.target?.value);
  keywordRef.value = e.target?.value;
}, 300);

const filtedMicroApps = computed<SortingItem[]>(() => {
  let keyword = keywordRef.value;
  keyword = keyword.trim();
  if (!keyword) {
    return microStore.sortingList;
  }
  return microStore.sortingList.filter((i) => {
    return (
      new RegExp(keyword, 'i').test(i.microDataRef.label || '') ||
      new RegExp(keyword, 'i').test(i.name || '')
    );
  });
});

const openDrawer = () => {
  drawerVisible.value = true;
};

const closeDrawer = () => {
  drawerVisible.value = false;
};
const handleTogglePin = (sortItem) => {
  if (sortItem.microDataRef.group === 'frequently') {
    handleUnpin(sortItem.name);
  } else {
    handlePin(sortItem.name);
  }
};

const handleClick = (sortItem) => {
  handleClickMicroApp(sortItem.name);
  closeDrawer();
};

const handleResearch = () => {
  inputRef.value.focus();
};

defineExpose({
  openDrawer,
  closeDrawer,
});
</script>

<template>
  <n-drawer
    v-model="drawerVisible"
    title="全部应用"
    position="left"
    :primary-button="false"
    :secondary-button="false"
  >
    <div class="drawer-content">
      <div class="search-bar">
        <n-input
          clearable
          ref="inputRef"
          placeholder="请搜索"
          prefix-icon="search"
          @keydown="debSearching"
          @clear="keywordRef = ''"
        />
      </div>
      <div
        class="micro-app-empty"
        v-if="keywordRef !== '' && filtedMicroApps.length < 1"
      >
        <n-empty size="large">
          <template #description>
            <p>
              暂无搜索结果,换个词试试?,
              <span class="research" @click="handleResearch">重新输入</span>
            </p>
          </template>
        </n-empty>
      </div>
      <perfect-scrollbar v-else ref="psScrollRef" :suppress-scroll-x="false">
        <div class="micro-app-list">
          <ui-micro-app-item
            mode="drawer"
            disable-tooltip
            class="drawer-micro-app-item"
            v-for="item in filtedMicroApps"
            :key="item.name"
            v-show="!item.microDataRef.hideOnMenu"
            :title="`${item.microDataRef.brand === 'alps' ? 'ALPS ' : ''}${
              item.microDataRef.label
            }`"
            :icon="item.microDataRef.icon"
            :size="`middle`"
            @click="handleClick(item)"
          >
            <template #icon-affix>
              <img
                class="badge-pinned"
                alt="pinned"
                :src="BadgePinned"
                v-if="item.microDataRef.group === 'frequently'"
              />
              <n-icon
                name="urgent_filled"
                color="red"
                class="alps-icon"
                v-if="item.microDataRef.brand === 'alps'"
              />
            </template>
            <template #action>
              <n-button
                type="text"
                kind="neutral"
                size="small"
                @click.stop="handleTogglePin(item)"
                style="margin-right: 6px"
                v-tooltip.top="
                  item.microDataRef.group === 'frequently'
                    ? '取消固定应用'
                    : '固定应用'
                "
                :icon="
                  item.microDataRef.group === 'frequently' ? 'pinned' : 'pin'
                "
              ></n-button>
            </template>
          </ui-micro-app-item>
        </div>
      </perfect-scrollbar>
    </div>
  </n-drawer>
</template>

<style scoped lang="scss">
.search-bar {
  padding: 24px 0 16px 0;
  flex: 0;
}
.content {
  height: 100%;
  overflow: hidden;
}
.drawer-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.micro-app-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 12px;
}
.micro-app-empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex: 1;
}
.badge-pinned {
  position: absolute;
  right: 0;
  bottom: 0;
}
.alps-icon {
  position: absolute;
  left: -6px;
  bottom: -6px;
}
.research {
  color: #178ca6;
  cursor: pointer;
}
</style>
